<template>
  <RygenMultiSelect
    :id="id"
    v-model="internalValue"
    option-label="label"
    option-value="value"
    :disabled="!editable"
    :options="optionsFiltered"
    @change="emit('update:model-value', $event.value)"
    @hide="emit('hide')"
  >
    <template
      #value="{ value, placeholder }: { value?: string[]; placeholder: string }"
    >
      <span v-if="!value?.length">{{ placeholder }}</span>
      <span
        v-else
        v-tooltip.left="{
          disabled: value.length < 2,
          value: selectedDocTypes,
        }"
      >
        {{ selectedDocTypes }}
      </span>
    </template>
  </RygenMultiSelect>
</template>

<script lang="ts" setup>
import { usePrimeVue } from 'primevue/config';
import { computed, PropType, Ref, ref, toRefs } from 'vue';

import { useTooltip } from '@rygen/rygen-ui/composables';
import {
  mapEnumToTranslatedDropdownValues,
  translateEnum,
} from '@rygen/rygen-ui/i18n';
import { CorsairDocumentType } from '@rygen/rygen-ui/model';

import { Order } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';

const props = defineProps({
  modelValue: {
    type: Array as PropType<CorsairDocumentType[]>,
    default: () => [],
  },
  id: {
    type: String,
    required: true,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, id, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'hide'): void;
  (event: 'update:model-value', newVal: CorsairDocumentType[]): void;
}>();

const { parentEntity } = useParentEntityStore();
const vTooltip = useTooltip();
defineExpose({
  $primevue: usePrimeVue(),
});

const internalValue: Ref<CorsairDocumentType[]> = ref(modelValue.value);
const options = mapEnumToTranslatedDropdownValues(
  CorsairDocumentType,
  'CorsairDocumentType',
);

const optionsFiltered = computed(() => {
  return options.filter(
    (type) =>
      !isShippingLabelTypeOnOrderInstance(type.value as CorsairDocumentType),
  );
});

const selectedDocTypes = computed(() => {
  return (
    internalValue.value
      .map((type) => translateEnum(type, 'CorsairDocumentType'))
      .join(', ') ?? ''
  );
});

const isOrderInstance = computed(
  (): boolean => parentEntity.value instanceof Order,
);

const isShippingLabelTypeOnOrderInstance = (type: CorsairDocumentType) => {
  return type === CorsairDocumentType.SHIPPING_LABEL && isOrderInstance.value;
};
</script>
